import ConnectWalletButton from '@/components/ConnectWalletButton';
import { getTaskApi } from '@/gql';
import { PointRecord, PointsTable } from '@/pages/Points/PointsTable';
import user from '@/stores/user';
import { TYPE } from '@/theme';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

const PageWrapper = styled.div`
    padding: 2rem;
    width: 100%;
`;

const ContentContainer = styled.div`
    width: 100%;

    margin: 0 auto;
    padding: 0 20px;
    background-color: #fff;
    border-radius: 20px;
    padding: 2rem;
`;

const CenterContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 60vh;
    gap: 24px;
    text-align: center;
`;

const HeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 40px;
    flex-wrap: wrap;
    gap: 20px;
`;

const BackButton = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 600;
    color: #000;

    &:hover {
        opacity: 0.7;
    }

    svg {
        font-size: 16px;
    }
`;

const Description = styled.div`
    font-size: 14px;
    color: #333;
    max-width: 600px;
    text-align: right;

    @media (max-width: 768px) {
        text-align: left;
    }
`;

const BannerCard = styled.div`
    background: linear-gradient(90deg, #ffffff 0%, #f0f7ff 100%);
    border-radius: 24px;
    padding: 2rem 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    /* margin-bottom: 40px; */
    position: relative;
    overflow: hidden;
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.05);

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
        padding: 24px;
    }
`;

const PointsInfo = styled.div`
    z-index: 1;
`;

const PointsValue = styled.div`
    font-size: 64px;
    font-weight: 700;
    color: #000;
    line-height: 1;
    margin-bottom: 8px;

    @media (max-width: 768px) {
        font-size: 48px;
    }
`;

const PointsLabel = styled.div`
    font-size: 16px;
    color: #ccc;
    font-weight: 500;
`;

const BannerImage = styled.img`
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    height: 120%;
    pointer-events: none;

    @media (max-width: 768px) {
        position: relative;
        transform: none;
        height: auto;
        width: 100%;
        margin-top: 20px;
    }
`;

const PointsPage = observer(() => {
    const history = useHistory();
    const { userDetail, bscTotalScore } = user;
    const isAuth = !!user.connected && !!userDetail?.id;
    const [recordsData, setRecordsData] = useState<PointRecord[]>([]);
    const [loading, setLoading] = useState(false);

    async function getUserTaskRecords() {
        const api = getTaskApi();
        setLoading(true);
        try {
            const response = await api.getUserTaskRecords({
                bizReq: {
                    templateCodes: ['lazbubuAdventureB', 'lazbubuLevelUpB'],
                },
            });
            if (response?.getUserTaskRecords?.data) {
                setRecordsData(
                    response.getUserTaskRecords.data.map((record) => ({
                        createTime: record?.createdAt || '',
                        taskName: record?.taskName || '',
                        score: record?.scoreInfo?.[0]?.score || 0,
                    })),
                );
            } else {
                setRecordsData([]);
            }
        } catch (error) {
            console.error('Failed to fetch user task records:', error);
            setRecordsData([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (isAuth) {
            getUserTaskRecords();
        }
    }, [isAuth, user.userId]);

    if (!isAuth) {
        return (
            <PageWrapper>
                <CenterContainer>
                    <TYPE.main fontSize={18} fontWeight={500}>
                        Please connect your wallet and login to view your points
                        history.
                    </TYPE.main>
                    <ConnectWalletButton />
                </CenterContainer>
            </PageWrapper>
        );
    }

    return (
        <PageWrapper>
            <ContentContainer>
                <HeaderSection>
                    <BackButton onClick={() => history.push('/')}>
                        <ArrowBackIosNewIcon />
                        Back
                    </BackButton>
                    <Description>
                        Help your Lazbubu mature by chatting and going on
                        adventures, while earning points.
                    </Description>
                </HeaderSection>
                <BannerCard>
                    <PointsInfo>
                        <PointsValue>{bscTotalScore}</PointsValue>
                        <PointsLabel>Total Points</PointsLabel>
                    </PointsInfo>
                    <BannerImage
                        src={require('@/assets/images/points/banner.png')}
                        alt="Points Banner"
                    />
                </BannerCard>
            </ContentContainer>
            <PointsTable records={recordsData} loading={loading} />
        </PageWrapper>
    );
});

export default PointsPage;
